import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Phone, PhoneOff, PhoneIncoming, PhoneOutgoing,
  Mic, MicOff, Volume2, VolumeX, Pause, Play,
  PhoneForwarded, User, Briefcase, Wifi, WifiOff,
  AlertCircle, CheckCircle, Loader2, X, Search,
  Hash, Delete, ChevronRight
} from 'lucide-react';
import { voipService } from '../../services/voipService';
import { ringCentralService } from '../../services/ringCentralService';
import { formatDateTime } from '../../utils/dateUtils';

const Softphone = ({
  extension,
  // Lifted state from parent
  phoneNumber: externalPhoneNumber,
  setPhoneNumber: externalSetPhoneNumber,
  callState: externalCallState,
  setCallState: externalSetCallState,
  connectionState: externalConnectionState,
  setConnectionState: externalSetConnectionState,
  activeCall: externalActiveCall,
  setActiveCall: externalSetActiveCall,
  incomingCallInfo: externalIncomingCallInfo,
  setIncomingCallInfo: externalSetIncomingCallInfo,
  onCallStateChange,
}) => {
  // Use external state if provided, otherwise use internal state
  const [internalPhoneNumber, internalSetPhoneNumber] = useState('');
  const [internalCallState, internalSetCallState] = useState('idle');
  const [internalActiveCall, internalSetActiveCall] = useState(null);
  const [internalConnectionState, internalSetConnectionState] = useState('disconnected');
  const [internalIncomingCallInfo, internalSetIncomingCallInfo] = useState(null);

  // Use external or internal state
  const phoneNumber = externalPhoneNumber !== undefined ? externalPhoneNumber : internalPhoneNumber;
  const setPhoneNumber = externalSetPhoneNumber || internalSetPhoneNumber;
  const callState = externalCallState !== undefined ? externalCallState : internalCallState;
  const setCallState = (newState) => {
    (externalSetCallState || internalSetCallState)(newState);
    onCallStateChange?.(newState);
  };
  const connectionState = externalConnectionState !== undefined ? externalConnectionState : internalConnectionState;
  const setConnectionState = externalSetConnectionState || internalSetConnectionState;
  const activeCall = externalActiveCall !== undefined ? externalActiveCall : internalActiveCall;
  const setActiveCall = externalSetActiveCall || internalSetActiveCall;
  const incomingCallInfo = externalIncomingCallInfo !== undefined ? externalIncomingCallInfo : internalIncomingCallInfo;
  const setIncomingCallInfo = externalSetIncomingCallInfo || internalSetIncomingCallInfo;

  // Internal-only state
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(100);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferNumber, setTransferNumber] = useState('');
  const [recentCalls, setRecentCalls] = useState([]);

  // Refs
  const callTimerRef = useRef(null);
  const initializingRef = useRef(false);
  const inputRef = useRef(null);
  const ringtoneRef = useRef(null);
  const notificationRef = useRef(null);

  // Global keyboard handler for dialpad
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle if typing in any input, textarea, or contenteditable element
      const tag = e.target.tagName;
      if ((tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) && e.target !== inputRef.current) return;

      // Handle number keys, *, #
      if (/^[0-9*#]$/.test(e.key)) {
        e.preventDefault();
        handleDigitClick(e.key);
        inputRef.current?.focus();
      }

      // Backspace to delete
      if (e.key === 'Backspace' && callState === 'idle') {
        e.preventDefault();
        handleBackspace();
      }

      // Enter to call
      if (e.key === 'Enter' && callState === 'idle' && phoneNumber && connectionState === 'registered') {
        e.preventDefault();
        handleCall();
      }

      // Escape to hang up or clear
      if (e.key === 'Escape') {
        if (['calling', 'ringing', 'active', 'hold'].includes(callState)) {
          handleHangup();
        } else if (phoneNumber) {
          setPhoneNumber('');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [callState, phoneNumber, connectionState]);

  // Request notification permission and set up ringtone on mount
  useEffect(() => {
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Create ringtone audio element
    ringtoneRef.current = new Audio('/assets/audio/ringtone.wav');
    ringtoneRef.current.loop = true;

    return () => {
      stopRingtone();
    };
  }, []);

  // Initialize RingCentral on mount
  useEffect(() => {
    initializeRingCentral();

    return () => {
      cleanup();
    };
  }, []);

  // Load recent calls
  useEffect(() => {
    loadRecentCalls();
  }, []);

  // Call duration timer
  useEffect(() => {
    if (callState === 'active' && !callTimerRef.current) {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else if (callState !== 'active' && callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
  }, [callState]);

  const loadRecentCalls = async () => {
    try {
      const calls = await voipService.getCallLogs({ limit: 5 });
      setRecentCalls(calls.items || []);
    } catch (error) {
      console.error('Failed to load recent calls:', error);
    }
  };

  // Play ringtone for incoming calls
  const playRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch(err => {
        console.log('Could not play ringtone:', err);
      });
    }
  };

  // Stop ringtone
  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  };

  // Show desktop notification for incoming call
  const showIncomingCallNotification = (callInfo) => {
    // Close any existing notification
    if (notificationRef.current) {
      notificationRef.current.close();
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      const callerName = callInfo?.fromName || formatPhoneNumber(callInfo?.from || 'Unknown');

      notificationRef.current = new Notification('Incoming Call', {
        body: `Call from ${callerName}`,
        icon: '/assets/images/virgo_logo.jpg',
        tag: 'virgo-incoming-call',
        requireInteraction: true,
        silent: false
      });

      // Focus window when notification is clicked
      notificationRef.current.onclick = () => {
        window.focus();
        notificationRef.current.close();
      };
    }
  };

  // Close incoming call notification
  const closeIncomingCallNotification = () => {
    if (notificationRef.current) {
      notificationRef.current.close();
      notificationRef.current = null;
    }
  };

  const initializeRingCentral = async () => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    try {
      setError(null);

      // Re-attach Softphone-specific callbacks (overrides global hook's handlers
      // so the Softphone UI gets call state updates while the phone tab is open)
      ringCentralService.on('onConnectionStateChange', (state) => {
        setConnectionState(state);
      });

      ringCentralService.on('onRegistered', () => {
        setConnectionState('registered');
        setError(null);
      });

      ringCentralService.on('onRegistrationFailed', (err) => {
        setConnectionState('error');
        setError('Failed to register with phone system');
      });

      ringCentralService.on('onIncomingCall', (callInfo, session) => {
        setIncomingCallInfo(callInfo);
        setCallState('incoming');
        setActiveCall({ session, ...callInfo });
        // Show desktop notification and play ringtone
        showIncomingCallNotification(callInfo);
        playRingtone();
      });

      ringCentralService.on('onCallAccepted', (session) => {
        setCallState('active');
        setCallDuration(0);
        // Stop ringtone and close notification when call is answered
        stopRingtone();
        closeIncomingCallNotification();
      });

      ringCentralService.on('onCallEnded', () => {
        // Stop ringtone and close notification when call ends
        stopRingtone();
        closeIncomingCallNotification();
        resetCallState();
        loadRecentCalls();
      });

      ringCentralService.on('onCallFailed', (err) => {
        // Stop ringtone and close notification when call fails
        stopRingtone();
        closeIncomingCallNotification();
        setError('Call failed');
        resetCallState();
      });

      ringCentralService.on('onCallHold', () => {
        setCallState('hold');
      });

      ringCentralService.on('onCallUnhold', () => {
        setCallState('active');
      });

      ringCentralService.on('onCallMuted', () => {
        setMuted(true);
      });

      ringCentralService.on('onCallUnmuted', () => {
        setMuted(false);
      });

      // Initialize the service (skip if global hook already registered)
      if (ringCentralService.getIsRegistered()) {
        console.log('[Softphone] Already registered via global hook, attaching callbacks only');
        setConnectionState('registered');
      } else {
        setConnectionState('connecting');
        await ringCentralService.initialize();
        // Check if registered after init (may have been handled by global hook)
        if (ringCentralService.getIsRegistered()) setConnectionState("registered");
      }

    } catch (error) {
      console.error('Failed to initialize RingCentral:', error);
      setConnectionState('error');
      setError(error.message || 'Failed to connect to phone system');
    } finally {
      initializingRef.current = false;
    }
  };

  const cleanup = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }
    stopRingtone();
    closeIncomingCallNotification();
    // NOTE: Do NOT dispose ringCentralService here.
    // The global useGlobalSoftphone hook in Layout owns the SIP lifecycle.
    // We only clean up UI state when leaving the phone tab.
  };

  const resetCallState = () => {
    setCallState('idle');
    setActiveCall(null);
    setPhoneNumber('');
    setCallDuration(0);
    setMuted(false);
    setIncomingCallInfo(null);
    setSelectedContact(null);
    setSelectedCase(null);
    setShowTransfer(false);
    setTransferNumber('');
  };

  const handleDigitClick = (digit) => {
    if (callState === 'active') {
      // Send DTMF during active call
      ringCentralService.sendDTMF(digit);
    }
    setPhoneNumber(prev => prev + digit);
  };

  const handleBackspace = () => {
    setPhoneNumber(prev => prev.slice(0, -1));
  };

  const handleCall = async () => {
    if (!phoneNumber || connectionState !== 'registered') return;

    try {
      setError(null);
      setCallState('calling');

      // Log call to backend
      const callLog = await voipService.makeCall(
        extension.id,
        phoneNumber,
        selectedContact?.id,
        selectedCase?.id
      );

      // Make actual WebRTC call
      const callResult = await ringCentralService.makeCall(phoneNumber);

      setActiveCall({
        ...callLog,
        session: callResult.session,
        phoneNumber: callResult.phoneNumber
      });

      setCallState('ringing');

    } catch (error) {
      console.error('Error making call:', error);
      setError(error.message || 'Failed to initiate call');
      resetCallState();
    }
  };

  const handleAnswer = async () => {
    try {
      stopRingtone();
      closeIncomingCallNotification();
      await ringCentralService.answerCall();
      setCallState('active');
      setCallDuration(0);
    } catch (error) {
      console.error('Error answering call:', error);
      setError('Failed to answer call');
    }
  };

  const handleDecline = async () => {
    try {
      stopRingtone();
      closeIncomingCallNotification();
      await ringCentralService.declineCall();
      resetCallState();
    } catch (error) {
      console.error('Error declining call:', error);
    }
  };

  const handleHangup = async () => {
    try {
      await ringCentralService.hangup();
      resetCallState();
    } catch (error) {
      console.error('Error hanging up:', error);
      resetCallState();
    }
  };

  const toggleMute = async () => {
    try {
      if (muted) {
        ringCentralService.unmute();
      } else {
        ringCentralService.mute();
      }
      setMuted(!muted);
    } catch (error) {
      console.error('Error toggling mute:', error);
    }
  };

  const toggleHold = async () => {
    try {
      if (callState === 'active') {
        await ringCentralService.hold();
      } else if (callState === 'hold') {
        await ringCentralService.unhold();
      }
    } catch (error) {
      console.error('Error toggling hold:', error);
    }
  };

  const handleTransfer = async () => {
    if (!transferNumber) return;
    try {
      await ringCentralService.transfer(transferNumber);
      resetCallState();
    } catch (error) {
      console.error('Error transferring call:', error);
      setError('Failed to transfer call');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (number) => {
    const cleaned = number.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return number;
  };

  const dialpadButtons = [
    [{ digit: '1', sub: '' }, { digit: '2', sub: 'ABC' }, { digit: '3', sub: 'DEF' }],
    [{ digit: '4', sub: 'GHI' }, { digit: '5', sub: 'JKL' }, { digit: '6', sub: 'MNO' }],
    [{ digit: '7', sub: 'PQRS' }, { digit: '8', sub: 'TUV' }, { digit: '9', sub: 'WXYZ' }],
    [{ digit: '*', sub: '' }, { digit: '0', sub: '+' }, { digit: '#', sub: '' }]
  ];

  const getConnectionIcon = () => {
    switch (connectionState) {
      case 'registered':
        return <Wifi className="w-4 h-4 text-green-400" />;
      case 'connecting':
        return <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />;
      case 'error':
        return <WifiOff className="w-4 h-4 text-red-400" />;
      default:
        return <WifiOff className="w-4 h-4 text-gray-400" />;
    }
  };

  const getConnectionLabel = () => {
    switch (connectionState) {
      case 'registered':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'error':
        return 'Connection Error';
      default:
        return 'Disconnected';
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-gradient-to-b from-virgo-navy-700 to-virgo-navy-800 rounded-2xl shadow-2xl overflow-hidden border border-virgo-navy-600">

        {/* Header */}
        <div className="px-6 py-4 border-b border-virgo-navy-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                extension?.status === 'available' ? 'bg-green-500/20' :
                extension?.status === 'busy' ? 'bg-red-500/20' :
                'bg-gray-500/20'
              }`}>
                <Phone className={`w-5 h-5 ${
                  extension?.status === 'available' ? 'text-green-400' :
                  extension?.status === 'busy' ? 'text-red-400' :
                  'text-gray-400'
                }`} />
              </div>
              <div>
                <div className="text-white font-medium">Ext. {extension?.extension || '---'}</div>
                <div className="text-sm text-virgo-teal-400 capitalize">{extension?.status || 'Unknown'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-virgo-navy-900 rounded-lg">
              {getConnectionIcon()}
              <span className="text-xs text-virgo-teal-400">{getConnectionLabel()}</span>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="px-4 py-3 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-400">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4 text-red-400" />
            </button>
          </div>
        )}

        {/* Call Display */}
        <div className="relative px-6 py-8">
          {/* Background gradient based on call state */}
          <div className={`absolute inset-0 ${
            callState === 'incoming' ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/10' :
            callState === 'active' ? 'bg-gradient-to-br from-blue-500/10 to-indigo-500/10' :
            callState === 'hold' ? 'bg-gradient-to-br from-yellow-500/10 to-amber-500/10' :
            callState === 'calling' || callState === 'ringing' ? 'bg-gradient-to-br from-cyan-500/10 to-indigo-500/10' :
            ''
          }`} />

          <div className="relative text-center">
            {/* Idle State */}
            {callState === 'idle' && (
              <>
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-virgo-navy-600 flex items-center justify-center">
                  <Phone className="w-10 h-10 text-virgo-teal-400" />
                </div>
                <div className="text-xl font-light text-white">Ready to call</div>
                <div className="text-sm text-virgo-teal-400 mt-1">
                  {connectionState === 'registered' ? 'Enter a number to dial' : 'Waiting for connection...'}
                </div>
              </>
            )}

            {/* Incoming Call */}
            {callState === 'incoming' && (
              <>
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center animate-pulse">
                  <PhoneIncoming className="w-10 h-10 text-green-400" />
                </div>
                <div className="text-xl font-light text-white">Incoming Call</div>
                <div className="text-2xl font-semibold text-white mt-2">
                  {incomingCallInfo?.fromName || formatPhoneNumber(incomingCallInfo?.from || 'Unknown')}
                </div>
                {incomingCallInfo?.from && (
                  <div className="text-sm text-virgo-teal-400 mt-1">
                    {formatPhoneNumber(incomingCallInfo.from)}
                  </div>
                )}

                {/* Answer/Decline buttons */}
                <div className="flex justify-center gap-6 mt-6">
                  <button
                    onClick={handleDecline}
                    className="p-4 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all shadow-lg shadow-red-500/25"
                  >
                    <PhoneOff className="w-7 h-7" />
                  </button>
                  <button
                    onClick={handleAnswer}
                    className="p-4 bg-green-500 hover:bg-green-600 text-white rounded-full transition-all shadow-lg shadow-green-500/25 animate-pulse"
                  >
                    <Phone className="w-7 h-7" />
                  </button>
                </div>
              </>
            )}

            {/* Calling/Ringing */}
            {(callState === 'calling' || callState === 'ringing') && (
              <>
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <PhoneOutgoing className="w-10 h-10 text-cyan-400 animate-pulse" />
                </div>
                <div className="text-xl font-light text-white">
                  {callState === 'calling' ? 'Calling...' : 'Ringing...'}
                </div>
                <div className="text-2xl font-semibold text-white mt-2">
                  {formatPhoneNumber(phoneNumber)}
                </div>
              </>
            )}

            {/* Active Call */}
            {(callState === 'active' || callState === 'hold') && (
              <>
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  callState === 'hold' ? 'bg-yellow-500/20' : 'bg-blue-500/20'
                }`}>
                  {callState === 'hold' ? (
                    <Pause className="w-10 h-10 text-yellow-400" />
                  ) : (
                    <Phone className="w-10 h-10 text-blue-400" />
                  )}
                </div>
                <div className="text-xl font-light text-white">
                  {callState === 'hold' ? 'On Hold' : 'In Call'}
                </div>
                <div className="text-2xl font-semibold text-white mt-2">
                  {formatPhoneNumber(phoneNumber || activeCall?.phoneNumber || 'Unknown')}
                </div>
                <div className="text-4xl font-mono text-virgo-gold-400 mt-4">
                  {formatDuration(callDuration)}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Phone Number Input */}
        {callState === 'idle' && (
          <div className="px-6 pb-4">
            <div className="relative">
              <input
                ref={inputRef}
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d*#]/g, ''))}
                placeholder="Enter phone number"
                autoFocus
                className="w-full text-2xl text-center font-mono bg-virgo-navy-900 border border-virgo-navy-600 rounded-xl px-4 py-3 text-white placeholder-virgo-navy-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
              {phoneNumber && (
                <button
                  onClick={handleBackspace}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-virgo-teal-400 hover:text-white"
                >
                  <Delete className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Dialpad */}
        {callState === 'idle' && (
          <div className="px-6 pb-4">
            <div className="grid grid-cols-3 gap-3">
              {dialpadButtons.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  {row.map(({ digit, sub }) => (
                    <button
                      key={digit}
                      onClick={() => handleDigitClick(digit)}
                      className="aspect-square bg-virgo-navy-600 hover:bg-virgo-navy-500 text-white rounded-2xl transition-all flex flex-col items-center justify-center shadow-lg"
                    >
                      <span className="text-2xl font-semibold">{digit}</span>
                      {sub && <span className="text-[10px] text-virgo-teal-500 tracking-wider">{sub}</span>}
                    </button>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Call Button (Idle) */}
        {callState === 'idle' && (
          <div className="px-6 pb-6">
            <button
              data-dial-button
              onClick={handleCall}
              disabled={!phoneNumber || connectionState !== 'registered'}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-gray-500 disabled:to-gray-600 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-500/25 disabled:shadow-none"
            >
              <Phone className="w-5 h-5" />
              Call
            </button>
          </div>
        )}

        {/* Call Controls (Active/Calling/Ringing) */}
        {['calling', 'ringing', 'active', 'hold'].includes(callState) && (
          <div className="px-6 pb-6 space-y-4">
            {/* Main Controls */}
            <div className="flex justify-center gap-4">
              <button
                onClick={toggleMute}
                disabled={callState !== 'active' && callState !== 'hold'}
                className={`p-4 rounded-full transition-all ${
                  muted
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/25'
                    : 'bg-virgo-navy-600 text-white hover:bg-virgo-navy-500'
                } disabled:opacity-50`}
              >
                {muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </button>

              <button
                onClick={toggleHold}
                disabled={callState !== 'active' && callState !== 'hold'}
                className={`p-4 rounded-full transition-all ${
                  callState === 'hold'
                    ? 'bg-yellow-500 text-white shadow-lg shadow-yellow-500/25'
                    : 'bg-virgo-navy-600 text-white hover:bg-virgo-navy-500'
                } disabled:opacity-50`}
              >
                {callState === 'hold' ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
              </button>

              <button
                onClick={handleHangup}
                className="p-4 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all shadow-lg shadow-red-500/25"
              >
                <PhoneOff className="w-6 h-6" />
              </button>

              <button
                onClick={() => setShowTransfer(!showTransfer)}
                disabled={callState !== 'active' && callState !== 'hold'}
                className={`p-4 rounded-full transition-all ${
                  showTransfer
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/25'
                    : 'bg-virgo-navy-600 text-white hover:bg-virgo-navy-500'
                } disabled:opacity-50`}
              >
                <PhoneForwarded className="w-6 h-6" />
              </button>
            </div>

            {/* Transfer Panel */}
            {showTransfer && (
              <div className="bg-virgo-navy-900 rounded-xl p-4 space-y-3">
                <div className="text-sm font-medium text-virgo-teal-400">Transfer to:</div>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={transferNumber}
                    onChange={(e) => setTransferNumber(e.target.value)}
                    placeholder="Enter number"
                    className="flex-1 px-4 py-2 bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg text-white placeholder-virgo-navy-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                  <button
                    onClick={handleTransfer}
                    disabled={!transferNumber}
                    className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-600 text-white rounded-lg transition-all"
                  >
                    Transfer
                  </button>
                </div>
              </div>
            )}

            {/* In-call Dialpad Toggle */}
            {(callState === 'active' || callState === 'hold') && (
              <div className="grid grid-cols-3 gap-2 pt-2">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map(digit => (
                  <button
                    key={digit}
                    onClick={() => handleDigitClick(digit)}
                    className="py-2 bg-virgo-navy-600 hover:bg-virgo-navy-500 text-white rounded-lg text-lg font-medium transition-all"
                  >
                    {digit}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recent Calls */}
        {callState === 'idle' && recentCalls.length > 0 && (
          <div className="border-t border-virgo-navy-600">
            <div className="px-4 py-3 text-sm font-medium text-virgo-teal-400">Recent Calls</div>
            <div className="divide-y divide-virgo-navy-600">
              {recentCalls.slice(0, 3).map((call) => (
                <button
                  key={call.id}
                  onClick={() => setPhoneNumber(call.to_number || call.from_number || '')}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-virgo-navy-700 transition-all"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    call.direction === 'inbound' ? 'bg-blue-500/20' : 'bg-green-500/20'
                  }`}>
                    {call.direction === 'inbound' ? (
                      <PhoneIncoming className="w-4 h-4 text-blue-400" />
                    ) : (
                      <PhoneOutgoing className="w-4 h-4 text-green-400" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-white text-sm">
                      {formatPhoneNumber(call.to_number || call.from_number || 'Unknown')}
                    </div>
                    <div className="text-xs text-virgo-teal-500">
                      {formatDateTime(call.created_at, { year: undefined })}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-virgo-teal-500" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Astraea AI Indicator */}
        {extension?.astraea_answer_enabled && callState === 'idle' && (
          <div className="px-4 py-3 bg-cyan-500/10 border-t border-cyan-500/20 flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
              A
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-cyan-300">Astraea AI Enabled</div>
              <div className="text-xs text-cyan-400">
                Answers after {extension.astraea_answer_rings} rings
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Softphone;
